// ---------------------------------------------------------------------------
// Hex rules — the Grid's cells as a flat-top hex lattice (DATA level)
// ---------------------------------------------------------------------------
//
// The Grid keeps its 315-subplot index scheme (subplotGrid: s = sc·15 + sr,
// column-major, sr 0 at the corridor band) — nothing stored changes. What
// changes is ADJACENCY: the board is a flat-top hex lattice in odd-q layout,
// odd columns shoved half a cell away from the band, so every spatial rule
// (blast footprints, distance intel, rocket coverage, scans, moats) now
// reads hex neighbours and hex distance, exactly what the player sees.
//
// Offset (sc, sr) ⇄ axial (q, r), Red Blob Games' odd-q convention:
//   q = sc                  r = sr − (sc − (sc & 1)) / 2
//   sc = q                  sr = r + (q − (q & 1)) / 2
// Cube coords are (x, y, z) = (q, −q−r, r); hex distance is the cube
// Manhattan distance / 2. The renderer (hexGeometry.js) uses the same
// convention, so a rule's cell set is the shape drawn on screen.
//
// Euclidean centre-to-centre distance, in hex-side units, is the ORDERING
// metric for "nearest first" (blast growth, animation sweeps): a cell's
// centre sits at ( 1.5·q ,  √3·(r + q/2) ).

import { SUB_COLS, SUB_ROWS, subIndex, subCoords } from './subplotGrid.js'

// The six axial directions, clockwise from east: E, NE, NW, W, SW, SE (with r
// growing toward the far edge, i.e. "down" on the self half).
export const HEX_DIRS = [[1, 0], [1, -1], [0, -1], [-1, 0], [-1, 1], [0, 1]]

// ── Offset ⇄ axial ─────────────────────────────────────────────────────────

export function offsetToAxial(sc, sr) {
    return { q: sc, r: sr - (sc - (sc & 1)) / 2 }
}

export function axialToOffset(q, r) {
    return { sc: q, sr: r + (q - (q & 1)) / 2 }
}

export function toAxial(s) {
    const { sc, sr } = subCoords(s)
    return offsetToAxial(sc, sr)
}

// Axial → flat index, or null when off the field.
export function fromAxial(q, r) {
    const { sc, sr } = axialToOffset(q, r)
    if (sc < 0 || sc >= SUB_COLS || sr < 0 || sr >= SUB_ROWS) return null
    return subIndex(sc, sr)
}

// ── Distance ───────────────────────────────────────────────────────────────

export function axialDistance(dq, dr) {
    return (Math.abs(dq) + Math.abs(dr) + Math.abs(dq + dr)) / 2
}

// Hex distance between two subplots — the number of steps between them.
export function hexDistance(a, b) {
    const A = toAxial(a), B = toAxial(b)
    return axialDistance(A.q - B.q, A.r - B.r)
}

// Squared Euclidean centre-to-centre distance of an axial offset, in
// hex-side units (ordering metric; squared avoids sqrt while keeping ties).
export function offsetDist2(dq, dr) {
    const x = 1.5 * dq
    const y = Math.sqrt(3) * (dr + dq / 2)
    return x * x + y * y
}

export function hexDist2(a, b) {
    const A = toAxial(a), B = toAxial(b)
    return offsetDist2(A.q - B.q, A.r - B.r)
}

// ── Neighbourhoods ─────────────────────────────────────────────────────────

// The up-to-6 in-bounds neighbours of a subplot, in HEX_DIRS order.
export function hexNeighbors(s) {
    const { q, r } = toAxial(s)
    const out = []
    for (const [dq, dr] of HEX_DIRS) {
        const n = fromAxial(q + dq, r + dr)
        if (n != null) out.push(n)
    }
    return out
}

// The ring at exactly distance k, as axial OFFSETS in walk order: starting at
// k steps south-west of the centre, six sides of k steps each, clockwise. The
// walk's CORNERS (the ring's six vertices, straight out along an axis) sit at
// walk index 0, k, 2k, …; every other index is an edge cell. Each entry is
// { dq, dr, i } so callers can pick corners vs edges. k = 0 is the centre.
export function ringOffsets(k) {
    if (k <= 0) return [{ dq: 0, dr: 0, i: 0 }]
    const out = []
    let q = HEX_DIRS[4][0] * k, r = HEX_DIRS[4][1] * k
    let i = 0
    for (let side = 0; side < 6; side++) {
        for (let step = 0; step < k; step++) {
            out.push({ dq: q, dr: r, i: i++ })
            q += HEX_DIRS[side][0]
            r += HEX_DIRS[side][1]
        }
    }
    return out
}

// Whether a walk index on ring k is one of the six corners.
export const isRingCorner = (i, k) => k > 0 && i % k === 0

// Every axial offset within distance k of the centre (the centre included),
// rings inside-out.
export function offsetsWithin(k) {
    const out = []
    for (let d = 0; d <= k; d++) for (const o of ringOffsets(d)) out.push([o.dq, o.dr])
    return out
}

// In-bounds subplots at exactly distance k from `s`, walk order.
export function hexRing(s, k) {
    const { q, r } = toAxial(s)
    const out = []
    for (const o of ringOffsets(k)) {
        const n = fromAxial(q + o.dq, r + o.dr)
        if (n != null) out.push(n)
    }
    return out
}

// In-bounds subplots within distance k of `s` (`s` included), as a Set.
export function withinDistance(s, k) {
    const { q, r } = toAxial(s)
    const out = new Set()
    for (const [dq, dr] of offsetsWithin(k)) {
        const n = fromAxial(q + dq, r + dr)
        if (n != null) out.add(n)
    }
    return out
}

// Applies axial offsets to a centre subplot: the in-bounds cells, in the
// offsets' order (off-field cells are dropped, never reflected).
export function cellsAtOffsets(s, offsets) {
    const { q, r } = toAxial(s)
    const out = []
    for (const [dq, dr] of offsets) {
        const n = fromAxial(q + dq, r + dr)
        if (n != null) out.push(n)
    }
    return out
}
